import React, { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { CalendarRange, Globe, MapPin, Info, User, Target } from "lucide-react";

/**
 * PricingMatrix (Msg 722 revision) — Editor de tarifas para Vehicle Categories.
 *
 * Estrutura: linhas = temporadas; colunas = campos da tarifa (Base € / €/km / €/min).
 * Cada coluna tem comissão-alvo (€ ou %). Cada célula: input tarifa parceiro + breakdown.
 *
 * Props:
 *   basePrices, baseAdmin: {[field]: value}   — admin reference
 *   priceFields: ["base_price_eur", "price_per_km", "price_per_minute"]
 *   seasons: Array<{id, name, scope, zone_key}>
 *   categories: (ignored in priceFields mode) | Array<{key, label}>
 *   zoneKey, overrides, onChange, showBaseRow, testIdPrefix
 */
export default function PricingMatrix({
  basePrices = {},
  seasons = [],
  categories = [],
  zoneKey,
  overrides = {},
  onChange,
  showBaseRow = true,
  priceFields = null,
  baseAdmin = {},
  testIdPrefix = "pm",
}) {
  const [commissionMode, setCommissionMode] = useState("eur"); // eur | pct
  const isPriceFieldsMode = Array.isArray(priceFields) && priceFields.length > 0;
  const columns = isPriceFieldsMode ? priceFields : categories.map((c) => c.key);
  const columnLabels = isPriceFieldsMode
    ? priceFields.map((k) => ({
        key: k,
        label: k === "base_price_eur" ? "Base €" : k === "price_per_km" ? "€/km" : k === "price_per_minute" ? "€/min" : k,
      }))
    : categories.map((c) => ({ key: c.key, label: c.label }));

  const applicableSeasons = useMemo(() => {
    return seasons.filter((s) => s.scope === "global" || (s.scope === "zone" && s.zone_key === zoneKey));
  }, [seasons, zoneKey]);

  const setBase = (col, value) => {
    const bp = { ...(overrides.base_prices || {}) };
    if (value === "" || value === null || value === undefined) delete bp[col];
    else bp[col] = Number(value);
    onChange({ ...overrides, base_prices: bp });
  };

  const setSeasonPrice = (seasonId, col, value) => {
    const sp = { ...(overrides.season_prices || {}) };
    const entry = { ...(sp[seasonId] || {}) };
    if (value === "" || value === null || value === undefined) delete entry[col];
    else entry[col] = Number(value);
    if (Object.keys(entry).length === 0) delete sp[seasonId];
    else sp[seasonId] = entry;
    onChange({ ...overrides, season_prices: sp });
  };

  const setCommission = (col, value) => {
    const c = { ...(overrides.commissions || {}) };
    if (value === "" || value === null || value === undefined) delete c[col];
    else c[col] = Number(value);
    onChange({ ...overrides, commissions: c });
  };

  const baseFor = (col) => (isPriceFieldsMode ? baseAdmin[col] : basePrices[col]);

  const targetEur = (col, admin) => {
    const raw = (overrides.commissions || {})[col];
    if (raw == null || raw === "") return null;
    const v = Number(raw);
    if (isNaN(v)) return null;
    if (commissionMode === "pct") return admin * (v / 100);
    return v;
  };

  const bpVal = overrides.base_prices || {};
  const spVal = overrides.season_prices || {};
  const commissions = overrides.commissions || {};

  const colWidth = Math.max(150, Math.floor(750 / columns.length));

  const renderCell = (col, currentVal, onSet, testId) => {
    const adminPrice = Number(baseFor(col)) || 0;
    const hasAdmin = adminPrice > 0;
    const target = targetEur(col, adminPrice);
    const suggestedClient = hasAdmin ? adminPrice + (target || 0) : (target || 0);
    const isEmpty = currentVal == null || currentVal === "";
    const client = isEmpty ? 0 : Number(currentVal);
    const ganha = client - adminPrice;
    const belowMinimum = hasAdmin && client > 0 && client < adminPrice - 0.001;
    const missTarget = target != null && target > 0 && !belowMinimum && !isEmpty && ganha < target - 0.001;

    return (
      <td key={col} className="p-2 align-top">
        <div className="flex flex-col gap-1">
          <Input
            type="number" min="0" step="0.5"
            max="9999"
            value={currentVal ?? ""}
            placeholder={hasAdmin ? `€${suggestedClient.toFixed(2)}` : "definir"}
            onChange={(e) => {
              const raw = e.target.value;
              if (raw === "") return onSet(col, "");
              const n = Number(raw);
              if (isNaN(n)) return;
              onSet(col, Math.min(9999, Math.max(0, n)));
            }}
            className={`w-full h-10 text-right !text-base font-bold ${belowMinimum ? "!border-red-500 bg-red-50 !text-red-700" : "text-[#0a0f1e]"} ${!hasAdmin && isEmpty ? "border-amber-300 bg-amber-50/40" : ""}`}
            data-testid={testId}
          />
          <div className="text-[10px] leading-tight font-mono truncate">
            {isEmpty ? (
              !hasAdmin ? (
                <div className="text-[9px] text-amber-700 uppercase tracking-wider truncate">sem base admin</div>
              ) : target && target > 0 ? (
                <div className="text-[9px] text-slate-400 uppercase tracking-wider truncate">sugere €{suggestedClient.toFixed(2)}</div>
              ) : (
                <div className="text-[9px] text-slate-400 uppercase tracking-wider truncate">min €{adminPrice.toFixed(2)}</div>
              )
            ) : (
              <>
                <div className={`truncate font-bold ${belowMinimum ? "text-red-600" : "text-emerald-700"}`}>
                  {belowMinimum ? "⚠" : "✓"} Comissão €{ganha.toFixed(2)}
                </div>
                {hasAdmin && (
                  <div className={`truncate ${belowMinimum ? "text-red-600 font-bold" : "text-slate-500"}`}>
                    DT+M €{Math.min(client, adminPrice).toFixed(2)}{belowMinimum ? ` <${adminPrice.toFixed(2)}` : ""}
                  </div>
                )}
                {missTarget && target != null && (
                  <div className="text-[9px] text-amber-700 font-semibold truncate">abaixo do alvo €{target.toFixed(2)}</div>
                )}
              </>
            )}
          </div>
        </div>
      </td>
    );
  };

  return (
    <div className="border border-slate-200 bg-white">
      <div className="p-3 border-b border-slate-200 bg-slate-50/50 flex items-start gap-2 text-[11px] text-slate-600">
        <Info className="w-3.5 h-3.5 text-[#f59e0b] mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <strong>Como funciona:</strong> defina a <strong>comissão-alvo</strong> (o que quer ganhar) por coluna e a <strong>tarifa cliente</strong> por célula.
          O sistema mostra em tempo real quanto ganha e quanto fica para DiTours+Motorista.
          <br /><strong className="text-red-600">Aviso vermelho:</strong> tarifa cliente inferior ao mínimo DiTours (base admin).
        </div>
        <div className="flex gap-1 items-center shrink-0">
          <span className="text-[9px] uppercase tracking-widest text-slate-500 font-bold">Comissão:</span>
          {[{ k: "eur", l: "€" }, { k: "pct", l: "%" }].map((m) => (
            <button
              key={m.k}
              type="button"
              onClick={() => setCommissionMode(m.k)}
              data-testid={`${testIdPrefix}-comm-mode-${m.k}`}
              className={`px-2 py-0.5 text-[10px] font-bold border transition-colors ${commissionMode === m.k ? "border-[#f59e0b] text-[#f59e0b] bg-[#f59e0b]/10" : "border-slate-300 text-slate-500 hover:border-slate-400"}`}
            >
              {m.l}
            </button>
          ))}
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm table-fixed" style={{ minWidth: `${260 + columns.length * 140}px` }}>
          <colgroup>
            <col style={{ width: "260px" }} />
            {columns.map((c) => (
              <col key={c} style={{ width: `${colWidth}px` }} />
            ))}
          </colgroup>
          <thead>
            <tr className="border-b-2 border-slate-200 bg-slate-50">
              <th className="p-2 text-left text-[10px] uppercase tracking-widest font-bold text-slate-500">Temporada</th>
              {columnLabels.map((c) => {
                const admin = Number(baseFor(c.key)) || 0;
                return (
                  <th key={c.key} className="p-2 text-right text-[10px] uppercase tracking-widest font-bold text-slate-500">
                    <div className="mb-1">{c.label}</div>
                    {admin > 0 && (
                      <div className="text-[9px] text-slate-400 font-mono normal-case mb-1">
                        Ref DiTours €{admin.toFixed(2)}
                      </div>
                    )}
                  </th>
                );
              })}
            </tr>
            <tr className="border-b border-slate-300 bg-[#f59e0b]/5">
              <th className="p-2 text-right text-[10px] uppercase tracking-widest font-bold text-[#f59e0b] flex items-center justify-end gap-1">
                <Target className="w-3 h-3" /> Comissão-alvo:
              </th>
              {columns.map((col) => (
                <th key={col} className="p-2 align-top">
                  <div className="flex items-stretch border border-slate-300 focus-within:border-[#f59e0b] focus-within:ring-2 focus-within:ring-[#f59e0b]/20 transition-colors bg-white overflow-hidden">
                    <span className="flex items-center justify-center px-2 bg-slate-50 text-slate-500 text-xs font-mono border-r border-slate-300 shrink-0">
                      {commissionMode === "eur" ? "€" : "%"}
                    </span>
                    <input
                      type="number" min="0" step="0.5"
                      max={commissionMode === "pct" ? "100" : "9999"}
                      value={commissions[col] ?? ""}
                      placeholder="0"
                      onChange={(e) => {
                        const raw = e.target.value;
                        if (raw === "") return setCommission(col, "");
                        const n = Number(raw);
                        if (isNaN(n)) return;
                        const capped = commissionMode === "pct" ? Math.min(100, Math.max(0, n)) : Math.min(9999, Math.max(0, n));
                        setCommission(col, capped);
                      }}
                      className="flex-1 min-w-0 px-2 h-8 text-right !text-sm outline-none bg-white"
                      style={{ MozAppearance: "textfield" }}
                      data-testid={`${testIdPrefix}-commission-${col}`}
                    />
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {showBaseRow && (
              <tr className="border-b border-slate-100" data-testid={`${testIdPrefix}-base-row`}>
                <td className="p-2 align-top">
                  <div className="text-sm font-semibold text-[#0a0f1e] flex items-center gap-1">
                    <Globe className="w-3.5 h-3.5 text-slate-400" /> Base (fora de temporada)
                  </div>
                  <div className="text-[10px] text-slate-500">Aplica-se quando nenhuma temporada activa cobre a data</div>
                </td>
                {columns.map((col) => renderCell(col, bpVal[col], setBase, `${testIdPrefix}-base-${col}`))}
              </tr>
            )}
            {applicableSeasons.length === 0 && (
              <tr>
                <td colSpan={columns.length + 1} className="p-4 text-center text-xs text-slate-400 italic">
                  Sem temporadas aplicáveis a esta zona.
                </td>
              </tr>
            )}
            {applicableSeasons.map((s) => (
              <tr key={s.id} className={`border-b border-slate-100 ${s.owner_type === "partner" ? "bg-[#fff8ec]/40" : ""}`} data-testid={`${testIdPrefix}-season-${s.id}`}>
                <td className="p-2 align-top">
                  <div className="text-sm font-semibold text-[#0a0f1e] flex items-center gap-1.5">
                    <CalendarRange className="w-3.5 h-3.5 text-[#f59e0b]" /> {s.name}
                    {s.owner_type === "partner" && (
                      <span className="inline-flex items-center gap-0.5 text-[8px] uppercase tracking-widest font-bold text-[#f59e0b] bg-[#f59e0b]/10 px-1.5 py-0.5 border border-[#f59e0b]/40">
                        <User className="w-2.5 h-2.5" /> Minha
                      </span>
                    )}
                  </div>
                  <div className="text-[10px] text-slate-500 flex items-center gap-1">
                    {s.scope === "global" ? <Globe className="w-2.5 h-2.5" /> : <MapPin className="w-2.5 h-2.5" />}
                    <span>{s.scope === "global" ? "Global" : `Zona: ${s.zone_key}`}</span>
                    <span>· prio {s.priority}</span>
                  </div>
                  <div className="text-[10px] text-slate-400 font-mono">
                    {(s.date_ranges || []).map((r, i) => (
                      <div key={i}>{String(r.start_day).padStart(2, "0")}/{String(r.start_month).padStart(2, "0")} → {String(r.end_day).padStart(2, "0")}/{String(r.end_month).padStart(2, "0")}</div>
                    ))}
                  </div>
                </td>
                {columns.map((col) => {
                  const entry = spVal[s.id] || {};
                  return renderCell(col, entry[col], (c, v) => setSeasonPrice(s.id, c, v), `${testIdPrefix}-season-${s.id}-${col}`);
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
